import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { storyApi } from '../services/api';

interface StoryInputProps {
  onSubmit: (storySetting: string, genre?: string, tone?: string) => void;
  onOpenFullTree?: () => void;
  onOpenInfiniteCanvas?: () => void;
  onOpenInteractiveStory?: (storySetting: string) => void;
  isLoading?: boolean;
  hasStoryData?: boolean;
  branchCount: number;
  setBranchCount: (count: number) => void;
}

const StoryInput: React.FC<StoryInputProps> = ({ onSubmit, onOpenFullTree, onOpenInfiniteCanvas, onOpenInteractiveStory, isLoading = false, hasStoryData = false, branchCount, setBranchCount }) => {
  const { t } = useTranslation();
  const [storySetting, setStorySetting] = useState('');
  const [genre, setGenre] = useState('');
  const [tone, setTone] = useState('');
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const [availableTones, setAvailableTones] = useState<string[]>([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [genresResponse, tonesResponse] = await Promise.all([
          storyApi.getGenres(),
          storyApi.getTones()
        ]);
        setAvailableGenres(genresResponse.genres);
        setAvailableTones(tonesResponse.tones);
      } catch (error) {
        console.error(t('errors.loadOptions'), error);
      }
    };
    loadOptions();
  }, [t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (storySetting.trim()) {
      onSubmit(
        storySetting.trim(), 
        genre || undefined, 
        tone || undefined
      );
      // 注意：App.tsx中的handleStorySubmit函数已经包含了自动打开故事树的逻辑
      // 不需要在这里调用onOpenFullTree()
    }
  };

  return (
    <form className="story-input-form" onSubmit={handleSubmit}>
      <div className="card-title">
        <span role="img" aria-label="setting">📝</span> {t('storyInput.title')}
      </div>
      <div style={{marginBottom: '15px'}}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#fff', opacity: 0.85 }}>
          {t('storyInput.storySetting.label')}
        </label>
        <textarea
          className=""
          value={storySetting}
          onChange={(e) => setStorySetting(e.target.value)}
          placeholder={t('storyInput.storySetting.placeholder')}
          disabled={isLoading}
          required
        />
      </div>
      <div className="branches" style={{marginTop: 0, marginBottom: '20px', gap: '18px', gridTemplateColumns: '1fr 1fr 1fr'}}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#fff', opacity: 0.85 }}>
            {t('storyInput.genre.label')}
          </label>
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            disabled={isLoading}
          >
            <option value="">{t('storyInput.genre.placeholder')}</option>
            {availableGenres.map(g => (
              <option key={g} value={g}>{t(`story.types.${g.toLowerCase()}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#fff', opacity: 0.85 }}>
            {t('storyInput.tone.label')}
          </label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            disabled={isLoading}
          >
            <option value="">{t('storyInput.tone.placeholder')}</option>
            {availableTones.map(t_key => (
              <option key={t_key} value={t_key}>{t(`story.styles.${t_key.toLowerCase()}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#fff', opacity: 0.85 }}>
            {t('storyInput.branchCount.label')}
          </label>
          <input
            type="number"
            min="2"
            max="5"
            value={branchCount}
            onChange={(e) => setBranchCount(parseInt(e.target.value) || 3)}
            disabled={isLoading}
          />
        </div>
      </div>
      <div style={{display: 'flex', justifyContent: 'center', width: '100%', gap: '18px'}}>
        <button
          type="submit"
          className="btn"
          disabled={!storySetting.trim() || isLoading}
          style={{marginTop: '15px'}}
        >
          {isLoading ? t('storyInput.buttons.generating') : t('storyInput.buttons.generate')}
        </button>
        <button
          type="button"
          className="btn"
          disabled={!storySetting.trim() || isLoading}
          style={{marginTop: '15px'}}
          onClick={() => onOpenInteractiveStory && onOpenInteractiveStory(storySetting.trim())}
        >
          {isLoading ? t('storyInput.buttons.generating') : t('storyInput.buttons.interactive')}
        </button>
      </div>
    </form>
  );
};

export default StoryInput;